import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  ListItem,
  Select,
  Spinner,
  Tag,
  Text,
  Tooltip,
  UnorderedList,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import ReactJson from "react-json-view";
import { useAuthenticatedRequest } from "../../hooks/useAuthenticatedRequest";
import { unwrapApiResponse } from "../../utils/unwrapApiResponse";
import { showApiErrorToast } from "../../utils/showApiToast";

export const CODIFY_BASE = "https://codify.poc.mrc.ai/api";
export const DEFAULT_SEGMENT = "Section 1";
const CHOOSE_ANOTHER = "__choose__";
const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);

/** "type → path (mode)", or the default placement. */
export const mappingText = (mapping) =>
  mapping?.path ? `${mapping.type || "?"} → ${mapping.path} (${mapping.mode || "set"})` : "default: property named after the type";

const asJson = (value) => {
  if (value == null) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

/**
 * Where a field sits in a JSON text, best effort: { start, end } of its
 * line, or null. Follows the names in yourJsonPath in order (indexes and
 * names not in the text are skipped), then finds the field after them.
 */
export const findFieldLine = (text, yourJsonPath, field) => {
  const json = String(text || "");
  const names = String(yourJsonPath || "").split(/[.[\]]+/).filter((n) => n && !/^\d+$/.test(n));
  const target = field || names[names.length - 1];
  if (!target) return null;
  let pos = 0;
  names.slice(0, -1).forEach((name) => {
    const at = json.indexOf(`"${name}"`, pos);
    if (at >= 0) pos = at;
  });
  let at = json.indexOf(`"${target}"`, pos);
  if (at < 0) at = json.indexOf(`"${target}"`);
  if (at < 0) return null;
  const start = json.lastIndexOf("\n", at) + 1;
  const end = json.indexOf("\n", at);
  return { start, end: end < 0 ? json.length : end };
};

/** One explained error: what's wrong, how to fix it, what's allowed and where it is in your JSON. */
const ErrorView = ({ error, onLocate }) => {
  const toast = useToast();
  const [details, setDetails] = useState(false);
  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `Copied "${value}"`, status: "success", duration: 1200 });
    } catch {
      toast({ title: "Couldn't copy", status: "error", duration: 1500 });
    }
  };
  return (
    <Box borderLeftWidth="3px" borderLeftColor="red.300" pl={2} py={0.5}>
      <Text fontSize="sm" fontWeight="semibold" color="red.800">{error.explanation || error.message || "This doesn't fit the schema."}</Text>
      {error.hint && <Text fontSize="xs" color="gray.700">{error.hint}</Text>}
      {Array.isArray(error.allowed) && error.allowed.length > 0 && (
        <HStack spacing={1} mt={1} flexWrap="wrap" rowGap={1}>
          <Text fontSize="xs" color="gray.600">Allowed here:</Text>
          {error.allowed.map((a, i) => (
            <Fragment key={a}>
              {i > 0 && <Text fontSize="xs" color="gray.400">·</Text>}
              <Tooltip label="Copy" hasArrow openDelay={300}>
                <Tag size="sm" fontFamily="mono" cursor="pointer" colorScheme="blue" variant="subtle" onClick={() => copy(a)}>{a}</Tag>
              </Tooltip>
            </Fragment>
          ))}
        </HStack>
      )}
      {(error.yourJsonPath || error.path) && (
        <Text fontSize="xs" color="gray.600" mt={1}>
          In your JSON:{" "}
          {onLocate ? (
            <Button size="xs" variant="link" fontFamily="mono" fontWeight="normal" colorScheme="blue"
              onClick={() => onLocate(error.yourJsonPath || error.path, error.field)}>
              {error.yourJsonPath || error.path}
            </Button>
          ) : (
            <Text as="span" fontFamily="mono">{error.yourJsonPath || error.path}</Text>
          )}
        </Text>
      )}
      {(error.kind || error.message) && (
        <>
          <Button size="xs" variant="link" color="gray.500" fontWeight="normal" onClick={() => setDetails(!details)}>
            {details ? "Hide details" : "Details"}
          </Button>
          <Collapse in={details} animateOpacity>
            <Text fontSize="xs" color="gray.500" fontFamily="mono" whiteSpace="pre-wrap">
              {[error.kind, error.message].filter(Boolean).join(" · ")}
            </Text>
          </Collapse>
        </>
      )}
    </Box>
  );
};

/**
 * One check: how many things to fix (or that it fits), where it went,
 * each problem and explained error, and the assembled JSON. onLocate, when
 * the check belongs to a JSON editor, shows an error's field in it.
 */
export const CheckView = ({ check, showSource = true, onLocate }) => {
  const [open, setOpen] = useState(false);
  const assembled = asJson(check.assembled);
  const problems = check.problems || [];
  const errors = check.errors || [];
  const toFix = problems.length + errors.length;
  return (
    <Box borderWidth="1px" borderRadius="md" p={3} bg="white">
      <HStack align="start" spacing={2}>
        {check.valid ? <CheckCircleIcon color="green.500" mt={0.5} /> : <WarningIcon color="red.500" mt={0.5} />}
        <Box flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="semibold" color={check.valid ? "green.700" : "red.700"}>
            {check.valid ? "Fits the specification" : `${Math.max(toFix, 1)} thing${Math.max(toFix, 1) === 1 ? "" : "s"} to fix`}
          </Text>
          {showSource && check.source && <Text fontSize="xs" color="gray.700">{check.source}</Text>}
          <Text fontSize="xs" color="gray.600">
            <Text as="span" fontFamily="mono">{mappingText(check.mapping)}</Text>
            {check.dataStandard ? ` · ${check.dataStandard}` : ""}
          </Text>
          {problems.length > 0 && (
            <UnorderedList fontSize="xs" color="orange.700" mt={1}>
              {problems.map((p, i) => <ListItem key={i}>{p}</ListItem>)}
            </UnorderedList>
          )}
          {errors.length > 0 && (
            <VStack align="stretch" spacing={2} mt={2}>
              {errors.map((e, i) => <ErrorView key={i} error={e} onLocate={onLocate} />)}
            </VStack>
          )}
          {check.assembled != null && (
            <>
              <Button size="xs" variant="link" colorScheme="blue" mt={2} onClick={() => setOpen(!open)}>
                {open ? "Hide" : "Show"} assembled JSON
              </Button>
              <Collapse in={open} animateOpacity>
                <Box mt={2} borderWidth="1px" borderRadius="md" p={2} maxH="320px" overflowY="auto">
                  {assembled && typeof assembled === "object" ? (
                    <ReactJson src={assembled} name={false} collapsed={3} displayDataTypes={false} displayObjectSize={false}
                      enableClipboard indentWidth={2} style={{ fontSize: "11px" }} />
                  ) : (
                    <Text fontSize="xs" whiteSpace="pre-wrap">{String(check.assembled)}</Text>
                  )}
                </Box>
              </Collapse>
            </>
          )}
        </Box>
      </HStack>
    </Box>
  );
};

/** The specifications for a collection, to choose from. */
export const loadSpecifications = async (get, collection) => {
  const res = await get(`${CODIFY_BASE}/specifications?page=1&pageSize=200${collection ? `&collection=${encodeURIComponent(collection)}` : ""}`);
  return asList(unwrapApiResponse(res.data));
};

/**
 * Tests the saved definition against specifications (GET
 * /definitions/{id}/test) and shows one card per specification. The chosen
 * specification (or none: every one that matches) and the segment re-run
 * the test. onSpecificationChange tells the editor which one is chosen.
 */
const DefinitionTestDrawer = ({ isOpen, onClose, definitionId, collection, specificationId, onSpecificationChange, runKey }) => {
  const toast = useToast();
  const { get } = useAuthenticatedRequest();
  const request = useRef(get);
  request.current = get;

  const [segment, setSegment] = useState(DEFAULT_SEGMENT);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [known, setKnown] = useState([]); // specifications seen: { _id, name }
  const [loadingOthers, setLoadingOthers] = useState(false);

  const remember = (list) =>
    setKnown((prev) => {
      const byId = Object.fromEntries(prev.map((s) => [s._id, s]));
      list.forEach((s) => s._id && (byId[s._id] = { ...byId[s._id], ...s }));
      return Object.values(byId);
    });

  const run = useCallback(
    async (specId, seg) => {
      if (!definitionId) return;
      setRunning(true);
      try {
        const params = new URLSearchParams();
        if (specId) params.set("specificationId", specId);
        if (seg?.trim()) params.set("segment", seg.trim());
        const res = await request.current(`${CODIFY_BASE}/definitions/${encodeURIComponent(definitionId)}/test?${params}`);
        const body = res.data;
        // The message (e.g. nothing matched) sits beside data in the envelope
        const data = unwrapApiResponse(body);
        const message = res._originalResponse?.response?.message ?? body?.response?.message ?? data?.message;
        setResult({ ...data, message });
        remember((data?.results || []).map((r) => ({ _id: r.specificationId, name: r.specificationName })));
      } catch (err) {
        showApiErrorToast(toast, err, { title: "Couldn't test the definition" });
      } finally {
        setRunning(false);
      }
    },
    [definitionId, toast]
  );

  // Run when the drawer opens (runKey changes each time the toolbar button is used)
  useEffect(() => {
    if (isOpen) run(specificationId, segment);
    // only when opened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, runKey]);

  const chooseSpecification = async (value) => {
    if (value === CHOOSE_ANOTHER) {
      setLoadingOthers(true);
      try {
        remember(await loadSpecifications(request.current, collection));
      } catch (err) {
        showApiErrorToast(toast, err, { title: "Couldn't load the specifications" });
      } finally {
        setLoadingOthers(false);
      }
      return;
    }
    onSpecificationChange(value || null);
    run(value || null, segment);
  };

  const results = result?.results || [];

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          Test against specification
          {result?.type && <Text fontSize="sm" fontWeight="normal" color="gray.500">{result.type}{result.collection ? ` · ${result.collection}` : ""}</Text>}
        </DrawerHeader>
        <DrawerBody py={4} bg="gray.50">
          <HStack spacing={3} align="flex-end" mb={4}>
            <FormControl flex="2">
              <FormLabel fontSize="sm">Specification</FormLabel>
              <Select size="sm" bg="white" value={specificationId || ""} onChange={(e) => chooseSpecification(e.target.value)} isDisabled={running || loadingOthers}>
                <option value="">Every specification that matches</option>
                {known.map((s) => <option key={s._id} value={s._id}>{s.name || s._id}</option>)}
                <option value={CHOOSE_ANOTHER}>{loadingOthers ? "Loading…" : "Choose another…"}</option>
              </Select>
            </FormControl>
            <FormControl flex="1">
              <FormLabel fontSize="sm">Segment (optional)</FormLabel>
              <Input size="sm" bg="white" value={segment} onChange={(e) => setSegment(e.target.value)}
                onBlur={() => run(specificationId, segment)} onKeyDown={(e) => e.key === "Enter" && run(specificationId, segment)} />
            </FormControl>
          </HStack>

          {running ? (
            <HStack><Spinner size="sm" /><Text fontSize="sm">Testing…</Text></HStack>
          ) : !result ? null : results.length === 0 ? (
            <Text fontSize="sm" color="gray.600">{result.message || "No specification matched this definition."}</Text>
          ) : (
            <VStack align="stretch" spacing={4}>
              {results.map((r) => (
                <Box key={r.specificationId} borderWidth="1px" borderRadius="md" bg="whiteAlpha.900" p={3}>
                  <HStack justify="space-between" mb={2} align="start">
                    <Box>
                      <Heading size="sm">{r.specificationName || r.specificationId}</Heading>
                      {r.contractSchemaId && <Text fontSize="xs" color="gray.500">{r.contractSchemaId} v{r.contractSchemaVersion}</Text>}
                    </Box>
                    <Badge colorScheme={r.valid ? "green" : "red"} borderRadius="full" px={2} textTransform="none">
                      {r.valid ? "Fits" : "Doesn't fit"}
                    </Badge>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {(r.checks || []).length === 0 ? (
                      <Text fontSize="xs" color="gray.500">Nothing to check.</Text>
                    ) : (
                      r.checks.map((c, i) => <CheckView key={i} check={c} />)
                    )}
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default DefinitionTestDrawer;
